import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import { useAppStore } from "../../providers/AppStoreProvider";
import { useAPI } from "../../providers/ApiProvider";
import { toastWithAsyncFetch } from "../../utils/helper";
import { TOASTIFY_OPTIONS } from "../../config/ReactToastify";

import "./Modal.scss";

export const ChapterModal = observer(() => {
  const store = useAppStore();
  const api = useAPI();
  const chapterNameRef = useRef(null);
  const [dangerConfirming, setDangerConfirming] = useState(false);

  useEffect(() => {
    if (store.chapterModal.mode === "edit") {
      const chapter = store.chapters.find((chapter) => chapter.id === store.chapterModal.entityId);

      chapterNameRef.current.value = chapter?.name;
    }
  }, [store.chapterModal.show]);

  const hideModalHandler = () => {
    setDangerConfirming(false);
    store.chapterModal.toggle();
  };

  const createChapterHandler = () => {
    store.chapterModal.setSubmitState(true);

    api
      .callApi("createChapter", {
        params: { courseId: store.course.id },
        body: {
          name: chapterNameRef.current.value,
          description: chapterNameRef.current.value,
        },
      })
      .then((res) => {
        store.createChapter(res.data.data.chapter);
        store.chapterModal.toggle();
        toast.success("Tạo chương thành công", TOASTIFY_OPTIONS);
      })
      .catch((e) => toast.error("Xảy ra lỗi, không thể tạo chương", TOASTIFY_OPTIONS))
      .finally((e) => store.chapterModal.setSubmitState(false));
  };

  const updateChapterHandler = () => {
    const newData = {
      id: store.chapterModal.entityId,
      name: chapterNameRef.current.value,
    };

    store.chapterModal.setSubmitState(true);
    api
      .callApi("updateChapter", {
        params: { pk: newData.id },
        body: { name: newData.name },
      })
      .then((res) => {
        store.updateChapter(res.data.data.chapter);
        hideModalHandler();
        toast.success("Cập nhật chương thành công", TOASTIFY_OPTIONS);
      })
      .catch((e) => toast.error("Xảy ra lỗi, không thể cập nhật chương", TOASTIFY_OPTIONS))
      .finally((e) => store.chapterModal.setSubmitState(false));
  };

  const deleteChapterHandler = () => {
    toastWithAsyncFetch(
      {
        loading: "Đang xoá chương...",
        success: "Xoá chương thành công",
        error: "Xoá chương thất bại!",
      },
      api.callApi.bind(null, "deleteChapter", { params: { pk: store.chapterModal.entityId } }),
      () => {
        hideModalHandler();
        store.deleteChapter(store.chapterModal.entityId);
      },
    );
  };

  return (
    <Modal size="md" show={store.chapterModal.show} onHide={hideModalHandler}>
      <Modal.Header closeButton className="border-0">
        <Modal.Title as="h3" className="fw-bold">
          {store.chapterModal.mode === "edit" ? "Sửa" : "Tạo"} chương học
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label className="fw-bold">Tên chương</Form.Label>
            <Form.Control
              name="name"
              ref={chapterNameRef}
              placeholder="Ví dụ: Làm việc với Thanh công cụ"
              className="mt-1 p-3 py-2 rounded-10"
            />
          </Form.Group>

          {store.chapterModal.mode === "edit" && store.hasPermission("delete_chapter") && (
            <Form.Group className="mt-4">
              <Form.Label className="fw-bold mb-1">Danger Zone</Form.Label>
              <div className="fs-7 text-black-50 mb-3">
                Xoá chương sẽ xoá tất cả các khoá nằm trong chương đó
              </div>

              {dangerConfirming ? (
                <div className="danger-confirm rounded-10">
                  <div className="fw-bold ms-1">Warning</div>
                  <div className="fs-7 text-black-50 ms-1 mb-3">Bạn có chắc chắn xoá chương?</div>
                  <div className="d-flex flex-row">
                    <Button
                      variant="danger"
                      className="rounded-pill me-2 px-4"
                      onClick={deleteChapterHandler}
                    >
                      Xoá chương
                    </Button>
                    <Button
                      variant="outline"
                      className="outline-0 text-black-50 rounded-pill border-0 p-2 px-4"
                      onClick={() => setDangerConfirming(false)}
                    >
                      Hủy
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div
                    role="button"
                    className="delete-btn rounded-10 py-2 px-3"
                    onClick={() => setDangerConfirming(true)}
                  >
                    Xoá chương này
                  </div>
                </div>
              )}
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button
          variant="outline"
          className="outline-0 text-black-50 rounded-pill border-0 p-2 px-4"
          onClick={hideModalHandler}
        >
          Hủy
        </Button>
        <Button
          className="fw-bold rounded-pill bg-gradient-orange border-0 p-2 px-4"
          onClick={store.chapterModal.mode === "edit" ? updateChapterHandler : createChapterHandler}
          disabled={store.chapterModal.isSubmitting}
        >
          {store.chapterModal.isSubmitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Đang gửi
            </>
          ) : store.chapterModal.mode === "edit" ? (
            "Sửa chương"
          ) : (
            "Tạo chương"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});
